import { useRef } from 'react';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useGetProjectActivityRangeQuery } from '../api/trackerEndpointsApi';

/**
 * Onboarding cares about lifetime runs, not the selected range.
 * `suggestedFrom` is null only when the project has no completed execution.
 * shortcut: a negative activity-range result stays cached until remount, so a
 * first run that lands outside the current range can keep onboarding until the
 * range includes that run.
 */
export function useProjectEverExecuted(
  projectId: string | null,
  rangeExecutions: number,
  summaryReady: boolean,
): { everExecuted: boolean; checking: boolean } {
  const seenRunProjectId = useRef<string | null>(null);
  if (projectId && rangeExecutions > 0) seenRunProjectId.current = projectId;
  const seenRunsHere = seenRunProjectId.current === projectId;
  const lifetimeArg =
    projectId && summaryReady && rangeExecutions === 0 && !seenRunsHere
      ? projectId
      : skipToken;
  const lifetime = useGetProjectActivityRangeQuery(lifetimeArg);
  return {
    everExecuted:
      rangeExecutions > 0 ||
      seenRunsHere ||
      Boolean(lifetime.data?.suggestedFrom) ||
      lifetime.isError,
    checking: lifetimeArg !== skipToken && lifetime.isLoading,
  };
}
